// Camera diagnostics utility to help debug webcam issues for candidates

export interface CameraDiagnostics {
  hasGetUserMedia: boolean;
  hasMediaDevices: boolean;
  userAgent: string;
  platform: string;
  isSecureContext: boolean;
  permissions?: {
    camera?: PermissionState;
  };
}

export async function getCameraDiagnostics(): Promise<CameraDiagnostics> {
  const diagnostics: CameraDiagnostics = {
    hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia,
    hasMediaDevices: !!navigator.mediaDevices,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    isSecureContext: window.isSecureContext,
  };

  // Try to get permission status if supported
  try {
    if (navigator.permissions) {
      const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
      diagnostics.permissions = {
        camera: cameraPermission.state,
      };
    }
  } catch (error) {
    console.log('[CameraDiagnostics] Permission query not supported:', error);
  }

  return diagnostics;
}

export function logCameraDiagnostics(context: string, diagnostics: CameraDiagnostics) {
  console.log(`[CameraDiagnostics] ${context}:`, {
    ...diagnostics,
    timestamp: new Date().toISOString(),
  });
}

export async function testCameraAccess(): Promise<{
  success: boolean;
  error?: string;
  streamId?: string;
  tracks?: number;
}> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        facingMode: 'user'
      },
      audio: false
    });

    const videoTracks = stream.getVideoTracks();
    const result = {
      success: true,
      streamId: stream.id,
      tracks: videoTracks.length,
    };

    // Clean up the test stream
    stream.getTracks().forEach(track => track.stop());

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown camera error',
    };
  }
}